import axiosInstance from "../lib/axios";

/**
 * Konten halaman Home yang dikelola dari admin (ARUNIKA_ADMIN -> menu Home
 * Page). Backend: GET /HomePage (publik, tanpa login).
 *
 * - Field teks kosong/belum diisi = null.
 * - Field gambar berisi URL absolut atau null.
 * - `updated_at` null = section belum pernah disimpan admin (website
 *   memakai konten bawaan, lihat lib/homePageContent.ts).
 * - Field yang translatable (judul, deskripsi, dst) punya pasangan
 *   `<field>_en` berisi versi Inggris -- null/kosong kalau admin belum
 *   mengisinya (lihat lib/homePageContent.ts untuk cara field ini dipilih
 *   sesuai bahasa aktif pengunjung).
 */
interface HomeSectionBase {
  updated_at: string | null;
}

export interface HeroSection extends HomeSectionBase {
  title: string | null;
  title_en: string | null;
  button_label: string | null;
  button_label_en: string | null;
  button_link: string | null;
  image: string | null;
}

export interface CategoryItem {
  id: number;
  title: string | null;
  title_en: string | null;
  description: string | null;
  description_en: string | null;
  link: string | null;
  image: string | null;
}

export interface CategoriesSection extends HomeSectionBase {
  items?: CategoryItem[];
}

export interface AboutSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  description: string | null;
  description_en: string | null;
  button_label: string | null;
  button_label_en: string | null;
  button_link: string | null;
  image: string | null;
}

export interface MaterialItem {
  id: number;
  name: string | null;
  description: string | null;
  description_en: string | null;
}

export interface MaterialsSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  items?: MaterialItem[];
}

export interface IndustryItem {
  id: number;
  name: string | null;
  name_en: string | null;
  icon: string | null;
}

export interface IndustriesSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  items?: IndustryItem[];
}

export interface RecentProjectSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  button_label: string | null;
  button_label_en: string | null;
  button_link: string | null;
}

export interface ClientItem {
  id: number;
  name: string | null;
  logo: string | null;
}

export interface ClientsSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  items?: ClientItem[];
}

export interface ClientStoryItem {
  id: number;
  name: string | null;
  quote: string | null;
  quote_en: string | null;
  photo: string | null;
}

export interface ClientStorySection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  items?: ClientStoryItem[];
}

export interface AchievementItem {
  id: number;
  value: string | null;
  label: string | null;
  label_en: string | null;
}

export interface AchievementsSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  items?: AchievementItem[];
}

export interface FooterSection extends HomeSectionBase {
  head_office: string | null;
  workshop: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  instagram: string | null;
  logo: string | null;
}

export interface AboutHeroSection extends HomeSectionBase {
  title: string | null;
  title_en: string | null;
  description: string | null;
  description_en: string | null;
  image: string | null;
}

export interface PointItem {
  id: number;
  text: string | null;
  text_en: string | null;
}

/** Dipakai untuk Visi dan Misi (bentuknya sama). */
export interface PointsSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  image: string | null;
  items?: PointItem[];
}

export interface CoreValueItem {
  id: number;
  title: string | null;
  title_en: string | null;
  description: string | null;
  description_en: string | null;
  icon: string | null;
}

export interface CoreValuesSection extends HomeSectionBase {
  section_title: string | null;
  section_title_en: string | null;
  items?: CoreValueItem[];
}

/**
 * Semua section yang dikelola dari admin: halaman Home dan About Us.
 * Section `header` sengaja tidak dipakai (lihat Navbar).
 */
export interface HomePageData {
  hero?: HeroSection;
  categories?: CategoriesSection;
  about?: AboutSection;
  materials?: MaterialsSection;
  industries?: IndustriesSection;
  recent_project?: RecentProjectSection;
  clients?: ClientsSection;
  client_story?: ClientStorySection;
  achievements?: AchievementsSection;
  footer?: FooterSection;
  about_hero?: AboutHeroSection;
  vision?: PointsSection;
  mission?: PointsSection;
  core_values?: CoreValuesSection;
}

export const getHomePage = async (): Promise<HomePageData> => {
  // Timeout lebih pendek dari default (10 detik): kalau backend lambat/mati,
  // pengunjung cepat mendapat konten bawaan, bukan menunggu lama.
  const { data } = await axiosInstance.get("/HomePage", { timeout: 6000 });

  return data.data;
};
